import React, { createContext, useContext, useState, useEffect } from 'react';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check for stored auth data on component mount
    const storedToken = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');

    if (storedToken && storedUser) {
      try {
        const userData = JSON.parse(storedUser);
        setToken(storedToken);
        setUser(userData);
      } catch (error) {
        // Clear invalid data
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      }
    }
    setLoading(false);
  }, []);

  const login = (loginResponse) => {
    const { user: userData, token: userToken } = loginResponse;
    setUser(userData);
    setToken(userToken);
    localStorage.setItem('token', userToken);
    localStorage.setItem('user', JSON.stringify(userData));
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  };

  const isAuthenticated = () => {
    return !!token && !!user;
  };

  const hasRole = (role) => {
    if (!user) return false;

    // Handle both string and numeric role values
    if (typeof user.role === 'string') {
      return user.role === role;
    } else if (typeof user.role === 'number') {
      // Map numeric values to role names
      const roleMap = {
        0: 'User',
        1: 'Manager',
        2: 'Admin'
      };
      return roleMap[user.role] === role;
    }
    return false;
  };

  const isAdmin = () => {
    return hasRole('Admin');
  };

  const isManager = () => {
    return hasRole('Manager');
  };

  const getAuthHeaders = () => {
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const handleApiResponse = async (response) => {
    if (response.status === 401) {
      // Token expired or invalid
      const authHeader = response.headers.get('WWW-Authenticate');
      if (authHeader && (authHeader.includes('token expired') || authHeader.includes('invalid_token'))) {
        console.log('Token expired or invalid, logging out...');
        logout();
        // Show a brief message before redirect
        alert('Ваша сессия истекла. Пожалуйста, войдите снова.');
        window.location.href = '/login';
        return null;
      }
    }
    return response;
  };

  const authenticatedFetch = async (url, options = {}) => {
    const headers = {
      ...options.headers,
      ...getAuthHeaders()
    };

    try {
      const response = await fetch(url, {
        ...options,
        headers
      });

      const handledResponse = await handleApiResponse(response);
      return handledResponse || response;
    } catch (error) {
      console.error('Fetch error:', error);
      throw error;
    }
  };

  const value = {
    user,
    token,
    loading,
    login,
    logout,
    isAuthenticated,
    hasRole,
    isAdmin,
    isManager,
    getAuthHeaders,
    authenticatedFetch,
    handleApiResponse
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
